// src/pages/CotizacionEditPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  obtenerCotizacion,
  actualizarCotizacion,
  duplicarCotizacion,
  sincronizarCatalogo,
  enviarCorreoCotizacion,
} from "../services/cotizacionService";
import { buscarClientePorClave, normalizarCliente, obtenerClientePorId } from "../services/clienteService";
import { buscarArticuloPorClave } from "../services/articuloService";
import Toast, { useToast } from "../components/Toast";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmtMX = (n) =>
  round2(n).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const calcPrecioVenta = (precioBase, aum) => {
  const base = num(precioBase);
  const pAum = num(aum);
  if (pAum > 0 && pAum < 100) return round2(base / (1 - pAum / 100));
  return round2(base);
};
const calcImportes = ({ precioBase, aum = 0, desc = 0, qty = 1 }) => {
  const cantidad = num(qty);
  const pVenta = calcPrecioVenta(precioBase, aum);
  const pUnitDesc =
    num(desc) > 0 ? round2(pVenta * (1 - num(desc) / 100)) : pVenta;
  return { pVenta, pUnitDesc, impDesc: round2(cantidad * pUnitDesc) };
};

/* ===== CellInput — FUERA del componente para evitar pérdida de foco ===== */
const CellInput = ({
  value,
  onChange,
  onBlur,
  onKeyDown,
  placeholder,
  type = "text",
  align = "left",
}) => (
  <input
    type={type}
    value={value ?? ""}
    onChange={onChange}
    onBlur={onBlur}
    onKeyDown={onKeyDown}
    placeholder={placeholder}
    step={type === "number" ? "0.0001" : undefined}
    style={{
      width: "100%",
      padding: "5px 7px",
      border: "1px solid var(--clr-border)",
      borderRadius: "var(--radius-sm)",
      fontSize: "var(--fs-sm)",
      fontFamily: "var(--font-sans)",
      background: "#fff",
      outline: "none",
      textAlign: align,
      boxSizing: "border-box",
    }}
    onFocus={(e) => {
      e.target.style.borderColor = "var(--clr-primary)";
      e.target.style.boxShadow = "var(--shadow-focus)";
    }}
    onBlurCapture={(e) => {
      e.target.style.borderColor = "var(--clr-border)";
      e.target.style.boxShadow = "none";
    }}
  />
);

export default function CotizacionEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cotNum, setCotNum] = useState(null);

  const [form, setForm] = useState({
    cliente: "",
    nombreCliente: "",
    fecha: new Date().toISOString().slice(0, 10),
    moneda: "P",
    tipoCambio: 0,
    observaciones: "",
    notasAdicionales: "",
    porcDsctoGlobal: 0,
    incrementoGlobal: 0,
    porFacturar: false,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [clienteMsg, setClienteMsg] = useState("");
  const [clienteLoading, setClienteLoading] = useState(false);
  const [clienteEmail, setClienteEmail] = useState("");
  const [rowMsgs, setRowMsgs] = useState({});
  const [items, setItems] = useState([]);
  const [showProfitModal, setShowProfitModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showDuplicarModal, setShowDuplicarModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({ destinatario: "", asunto: "", cuerpo: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const { toasts, closeToast, error: toastError, warning: toastWarning, success: toastSuccess } = useToast();
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const dragSrcIdx = useRef(null);

  const setRowMsg = (idx, msg) =>
    setRowMsgs((m) => ({ ...m, [idx]: msg || "" }));

  const safeItems = useMemo(
    () =>
      Array.isArray(items)
        ? items.filter((it) => it && typeof it === "object")
        : [],
    [items],
  );

  const addRow = () =>
    setItems((arr) => [
      ...arr,
      {
        articulo: "",
        descripcion: "",
        unidadMedida: "PZA",
        claveUnidad: "",
        codSat: "",
        cantidad: 1,
        precioCompra: 0,
        porcDescto: 0,
        porcIncremento: 0,
        codigoFabricante: "",
      },
    ]);

  const openDeleteModal = (idx) => {
    setRowToDelete(idx);
    setShowDeleteModal(true);
  };
  const closeDeleteModal = () => {
    setRowToDelete(null);
    setShowDeleteModal(false);
  };
  const confirmRmRow = () => {
    if (rowToDelete !== null)
      setItems((arr) => arr.filter((_, i) => i !== rowToDelete));
    closeDeleteModal();
  };

  const moveRow = (from, to) => {
    if (from === to) return;
    setItems((arr) => {
      const next = [...arr];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setRowMsgs({});
  };

  const calcTotalProfit = () => {
    let totalCompra = 0,
      totalVenta = 0;
    for (const it of safeItems) {
      totalCompra += round2(num(it.cantidad) * num(it.precioCompra));
      totalVenta += calcImportes({
        precioBase: it.precioCompra,
        aum: it.porcIncremento,
        desc: it.porcDescto,
        qty: it.cantidad,
      }).impDesc;
    }
    let ventaNeta = totalVenta;
    if (num(form.porcDsctoGlobal) > 0)
      ventaNeta *= 1 - num(form.porcDsctoGlobal) / 100;
    if (num(form.incrementoGlobal) > 0)
      ventaNeta *= 1 + num(form.incrementoGlobal) / 100;
    return {
      totalImporteCompra: round2(totalCompra),
      totalImporteVenta: round2(ventaNeta),
      utilidad: round2(ventaNeta - totalCompra),
    };
  };
  const totalProfit = useMemo(calcTotalProfit, [
    safeItems,
    form.porcDsctoGlobal,
    form.incrementoGlobal,
  ]); // eslint-disable-line

  const onChangeHeader = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({
      ...f,
      [name]: ["porcDsctoGlobal", "incrementoGlobal", "tipoCambio"].includes(
        name,
      )
        ? Number(value)
        : value,
    }));
  };

  const onChangeItem = (idx, name, value) => {
    setItems((arr) => {
      if (!Array.isArray(arr) || !arr[idx]) return arr;
      return arr.map((it, i) =>
        i === idx
          ? {
              ...it,
              [name]: [
                "cantidad",
                "precioCompra",
                "porcDescto",
                "porcIncremento",
              ].includes(name)
                ? Number(value)
                : value,
            }
          : it,
      );
    });
  };

  const onClienteBlur = async () => {
    const clave = form.cliente?.trim();
    if (!clave) {
      setClienteMsg("");
      setForm((f) => ({ ...f, nombreCliente: "" }));
      return;
    }
    setClienteMsg("");
    setClienteLoading(true);
    try {
      const empresaId = Number(localStorage.getItem("empresaId") || 1);
      const cli = await buscarClientePorClave(empresaId, clave);
      if (!cli) {
        setForm((f) => ({ ...f, nombreCliente: "" }));
        setClienteMsg("El cliente no existe.");
        return;
      }
      setForm((f) => ({
        ...f,
        nombreCliente: cli.nombre ?? cli.razonSocial ?? cli.nombreCliente ?? "",
      }));
      const clienteId = cli?.id ?? cli?.ID;
      if (clienteId) {
        obtenerClientePorId(clienteId)
          .then((full) => {
            const norm = normalizarCliente(full?.data?.data ?? full?.data ?? full);
            setClienteEmail(norm?.email || "");
          })
          .catch(() => {});
      }
    } catch {
      setForm((f) => ({ ...f, nombreCliente: "" }));
      setClienteMsg("No se pudo validar el cliente.");
    } finally {
      setClienteLoading(false);
    }
  };

  const lookupArticulo = async (idx) => {
    const claveRaw = (items[idx]?.articulo || "").trim();
    if (!claveRaw) {
      setRowMsg(idx, "");
      return;
    }
    const clave = claveRaw.toUpperCase();
    try {
      setRowMsg(idx, "Buscando...");
      const empresaId = Number(localStorage.getItem("empresaId") || 1);
      const art = await buscarArticuloPorClave(empresaId, clave);
      if (!art) {
        setRowMsg(idx, "Articulo no encontrado.");
        return;
      }
      const artClave = (art.articulo ?? art.clave ?? art.Clave ?? "")
        .toString()
        .trim()
        .toUpperCase();
      if (artClave !== clave) {
        setRowMsg(idx, "Articulo no encontrado (clave exacta).");
        return;
      }
      setItems((arr) =>
        arr.map((it, i) =>
          i === idx
            ? {
                ...it,
                articulo: artClave,
                descripcion: art.descripcion || it.descripcion || "",
                unidadMedida:
                  art.unidadMedida ??
                  art.UnidadMedida ??
                  it.unidadMedida ??
                  "PZA",
                claveUnidad:
                  art.claveUnidad ?? art.ClaveUnidad ?? it.claveUnidad ?? "",
                codSat: art.codSat ?? it.codSat ?? "",
                precioCompra: Number.isFinite(
                  Number(art.precioCompra ?? art.precio),
                )
                  ? Number(art.precioCompra ?? art.precio)
                  : Number(it.precioCompra) || 0,
              }
            : it,
        ),
      );
      setRowMsg(idx, "");
    } catch {
      setRowMsg(idx, "Articulo no encontrado.");
    }
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await obtenerCotizacion(id);
        if (cancel) return;
        setForm({
          cliente: data.cliente,
          nombreCliente: data.nombreCliente || data.cliente,
          fecha: data.fecha
            ? String(data.fecha).slice(0, 10)
            : new Date().toISOString().slice(0, 10),
          moneda: data.moneda || "P",
          tipoCambio: data.tipoCambio || 0,
          observaciones: data.observaciones || "",
          notasAdicionales: data.notasAdicionales || "",
          porcDsctoGlobal: data.porcDscto || 0,
          incrementoGlobal: data.incrementoGlobal || 0,
          porFacturar: data.porFacturar ?? false,
        });
        setCotNum(data.cotizacion ?? null);
        // Cargar email del cliente en segundo plano
        if (data.cliente) {
          const empId = Number(localStorage.getItem("empresaId") || 1);
          buscarClientePorClave(empId, data.cliente)
            .then(async (raw) => {
              if (cancel) return;
              const clienteId = raw?.id ?? raw?.ID;
              if (!clienteId) return;
              const full = await obtenerClientePorId(clienteId);
              const norm = normalizarCliente(full?.data?.data ?? full?.data ?? full);
              if (!cancel) setClienteEmail(norm?.email || "");
            })
            .catch(() => {});
        }
        setItems(
          (data.cotizacionesDetalle || [])
            .filter((d) => d && typeof d === "object")
            .map((d) => ({
              articulo: d.articulo,
              descripcion: d.descripcion,
              unidadMedida: d.unidadmedida,
              claveUnidad:
                d.claveUnidad ?? d.ClaveUnidad ?? d.clave_unidad ?? "",
              codSat: d.codSat,
              cantidad: num(d.cantidad || 0),
              precioCompra: num(d.precioCompra ?? d.precio_compra ?? 0),
              porcDescto: num(d.porcDescto ?? d.porcdescto ?? 0),
              porcIncremento: num(d.porcIncremento ?? d.porc_incremento ?? 0),
              codigoFabricante: d.codigoFabricante ?? d.codigo_fabricante ?? "",
            })),
        );
      } catch (e) {
        toastError(e.message || "No se pudo cargar la cotizacion");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [id]);

  const subtotalAntesGlobal = useMemo(
    () =>
      round2(
        items.reduce(
          (s, it) =>
            s +
            calcImportes({
              precioBase: it.precioCompra,
              aum: it.porcIncremento,
              desc: it.porcDescto,
              qty: it.cantidad,
            }).impDesc,
          0,
        ),
      ),
    [items],
  );
  const subtotal = useMemo(() => {
    let s = subtotalAntesGlobal;
    if (num(form.porcDsctoGlobal) > 0) s *= 1 - num(form.porcDsctoGlobal) / 100;
    if (num(form.incrementoGlobal) > 0)
      s *= 1 + num(form.incrementoGlobal) / 100;
    return round2(s);
  }, [subtotalAntesGlobal, form.porcDsctoGlobal, form.incrementoGlobal]);
  const iva = useMemo(() => round2(subtotal * 0.16), [subtotal]);
  const total = useMemo(() => round2(subtotal + iva), [subtotal, iva]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.cliente?.trim()) { toastWarning("Captura la clave del cliente."); return; }
    if (!form.nombreCliente?.trim()) { toastWarning("El cliente no existe."); return; }
    if (!items.length) { toastWarning("Agrega al menos un renglon."); return; }
    try {
      setSaving(true);
      await actualizarCotizacion(id, {
        cliente: form.cliente,
        nombreCliente: form.nombreCliente || form.cliente,
        fecha: form.fecha,
        moneda: form.moneda,
        tipoCambio: form.moneda === "D" ? num(form.tipoCambio || 0) : 0,
        observaciones: form.observaciones,
        notasAdicionales: form.notasAdicionales,
        porcDsctoGlobal: num(form.porcDsctoGlobal || 0),
        incrementoGlobal: num(form.incrementoGlobal || 0),
        porFacturar: form.porFacturar,
        detalles: items.map((it) => ({
          articulo: it.articulo,
          descripcion: it.descripcion,
          unidadMedida: it.unidadMedida,
          claveUnidad: it.claveUnidad,
          codSat: it.codSat || null,
          cantidad: num(it.cantidad ?? 0),
          precioUnitario: num(it.precioCompra ?? 0),
          porcDescto: num(it.porcDescto ?? 0),
          porcIncremento: num(it.porcIncremento ?? 0),
          codigoFabricante: it.codigoFabricante || null,
        })),
      });
      navigate("/cotizaciones");
    } catch (err) {
      toastError(err?.response?.data?.detail || err?.response?.data || err?.message || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleSincronizarCatalogo = () => {
    const itemsConClave = safeItems.filter((it) => it.articulo?.trim());
    if (!itemsConClave.length)
      return toastWarning("No hay artículos con clave para sincronizar.");
    setShowSyncModal(true);
  };

  const confirmarSincronizar = async () => {
    setShowSyncModal(false);
    try {
      setSyncing(true);
      const empresaId = Number(localStorage.getItem("empresaId") || 1);
      const { data } = await sincronizarCatalogo(id, empresaId);
      toastSuccess(`Catálogo sincronizado — Nuevos: ${data.creados}, actualizados: ${data.actualizados}`);
    } catch (err) {
      toastError(err?.response?.data?.detail || err?.response?.data || err?.message || "No se pudo sincronizar el catálogo");
    } finally {
      setSyncing(false);
    }
  };

  const handleDuplicar = () => setShowDuplicarModal(true);

  const confirmarDuplicar = async () => {
    setShowDuplicarModal(false);
    try {
      setDuplicating(true);
      const { data } = await duplicarCotizacion(id);
      navigate(`/cotizaciones/${data.id}/editar`);
    } catch (err) {
      toastError(err?.response?.data?.detail || err?.response?.data || err?.message || "No se pudo duplicar la cotización");
    } finally {
      setDuplicating(false);
    }
  };

  const handleAbrirEmail = () => {
    const numCot = cotNum ?? id;
    setEmailForm({
      destinatario: clienteEmail,
      asunto: `Cotización #${numCot} – LMD Integradora`,
      cuerpo: `Estimado(a) ${form.nombreCliente || form.cliente},\n\nAnexamos la cotización #${numCot} solicitada, por un importe de $${fmtMX(total)} ${form.moneda === "D" ? "USD" : "MXN"} (IVA incluido).\n\nQuedamos a sus órdenes para cualquier aclaración.\n\nAtentamente,\nEquipo de Ventas\nLMD Integradora\nventas@lmdint.mx`,
    });
    setShowEmailModal(true);
  };

  const handleEnviarEmail = async () => {
    // Acepta varios correos separados por ';' o ',' — se valida cada uno
    const correos = (emailForm.destinatario || "").split(/[;,]/).map((s) => s.trim()).filter(Boolean);
    if (correos.length === 0) { toastWarning("Ingresa el correo del destinatario."); return; }
    const invalidos = correos.filter((c) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c));
    if (invalidos.length > 0) { toastWarning(`Correo(s) inválido(s): ${invalidos.join(", ")}`); return; }
    try {
      setSendingEmail(true);
      await enviarCorreoCotizacion(id, {
        destinatario: emailForm.destinatario.trim(),
        asunto: emailForm.asunto,
        cuerpo: emailForm.cuerpo,
        empresaId: Number(localStorage.getItem("empresaId") || 1),
      });
      setShowEmailModal(false);
      toastSuccess(`Correo enviado a ${emailForm.destinatario}`);
    } catch (err) {
      toastError(err?.response?.data?.detail || err?.response?.data || err?.message || "No se pudo enviar el correo");
    } finally {
      setSendingEmail(false);
    }
  };

  const handlePrint = () => {
    window.open(`/cotizaciones/print/${id}`, "_blank");
  };

  const cellStyle = {
    padding: "8px 10px",
    borderBottom: "1px solid var(--clr-border-subtle)",
    verticalAlign: "middle",
  };
  const cellNum = { ...cellStyle, textAlign: "right" };

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 300,
          gap: 10,
          color: "var(--clr-text-muted)",
        }}
      >
        <div className="lmd-spinner" /> Cargando cotizacion...
      </div>
    );

  return (
    <div style={{ maxWidth: 1340 }}>
      <Toast toasts={toasts} onClose={closeToast} />
      <div className="lmd-page-header">
        <div className="lmd-page-header-row">
          <div>
            <h1 className="lmd-page-title">Cotizacion #{cotNum ?? id}</h1>
            <p className="lmd-page-subtitle">
              Edicion de encabezado y renglones
            </p>
          </div>
          <button
            type="button"
            className="lmd-btn lmd-btn-secondary"
            onClick={() => navigate("/cotizaciones")}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Regresar
          </button>
        </div>
      </div>

      <form onSubmit={onSubmit} style={{ display: "grid", gap: "var(--sp-4)" }}>
        <div className="lmd-card">
          <div className="lmd-card-header">
            <h3 className="lmd-card-title">Datos del encabezado</h3>
            {form.porFacturar && (
              <span className="lmd-badge lmd-badge-warning">Por facturar</span>
            )}
          </div>
          <div
            className="lmd-card-body"
            style={{ display: "grid", gap: "var(--sp-4)" }}
          >
            <div className="lmd-grid lmd-grid-1-2">
              <div className="lmd-field">
                <label className="lmd-label">Cliente *</label>
                <input
                  className="lmd-input"
                  name="cliente"
                  value={form.cliente}
                  onChange={onChangeHeader}
                  onBlur={onClienteBlur}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onClienteBlur();
                    }
                  }}
                  placeholder="Clave del cliente (ej. HUIZACHE)"
                />
                {clienteLoading && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginTop: 4,
                      fontSize: "var(--fs-xs)",
                      color: "var(--clr-text-muted)",
                    }}
                  >
                    <div
                      className="lmd-spinner"
                      style={{ width: 12, height: 12 }}
                    />{" "}
                    Buscando...
                  </div>
                )}
                {clienteMsg && (
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: "var(--fs-xs)",
                      color: "var(--clr-danger)",
                    }}
                  >
                    {clienteMsg}
                  </p>
                )}
              </div>
              <div className="lmd-field">
                <label className="lmd-label">Nombre del cliente</label>
                <input
                  className="lmd-input"
                  name="nombreCliente"
                  value={form.nombreCliente}
                  onChange={onChangeHeader}
                  placeholder="Se llena automaticamente"
                  style={{
                    background: form.nombreCliente ? "#fff" : "var(--clr-bg)",
                  }}
                />
              </div>
            </div>

            <div className={`lmd-grid ${form.moneda === "D" ? "lmd-grid-3" : "lmd-grid-2"}`}>
              <div className="lmd-field">
                <label className="lmd-label">Fecha</label>
                <input
                  className="lmd-input"
                  type="date"
                  name="fecha"
                  value={form.fecha}
                  onChange={onChangeHeader}
                />
              </div>
              <div className="lmd-field">
                <label className="lmd-label">Moneda</label>
                <select
                  className="lmd-select"
                  name="moneda"
                  value={form.moneda}
                  onChange={onChangeHeader}
                  style={{ width: "100%" }}
                >
                  <option value="P">MXN — Pesos</option>
                  <option value="D">USD — Dolares</option>
                </select>
              </div>
              {form.moneda === "D" && (
                <div className="lmd-field">
                  <label className="lmd-label">Tipo de cambio</label>
                  <input
                    className="lmd-input"
                    type="number"
                    name="tipoCambio"
                    value={form.tipoCambio}
                    step="0.0001"
                    onChange={onChangeHeader}
                  />
                </div>
              )}
            </div>

            <div className="lmd-grid lmd-grid-2">
              <div className="lmd-field">
                <label className="lmd-label">Observaciones</label>
                <input
                  className="lmd-input"
                  name="observaciones"
                  value={form.observaciones}
                  onChange={onChangeHeader}
                />
              </div>
              <div className="lmd-field">
                <label className="lmd-label">Notas adicionales</label>
                <input
                  className="lmd-input"
                  name="notasAdicionales"
                  value={form.notasAdicionales}
                  onChange={onChangeHeader}
                />
              </div>
            </div>

            <div className="lmd-grid lmd-grid-3" style={{ alignItems: "end" }}>
              <div className="lmd-field">
                <label className="lmd-label">% Descuento global</label>
                <input
                  className="lmd-input"
                  type="number"
                  name="porcDsctoGlobal"
                  value={form.porcDsctoGlobal}
                  step="0.01"
                  onChange={onChangeHeader}
                />
              </div>
              <div className="lmd-field">
                <label className="lmd-label">% Incremento global</label>
                <input
                  className="lmd-input"
                  type="number"
                  name="incrementoGlobal"
                  value={form.incrementoGlobal}
                  step="0.01"
                  onChange={onChangeHeader}
                />
              </div>
              <div className="lmd-field">
                <label className="lmd-label">Por facturar</label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    cursor: "pointer",
                    padding: "9px 12px",
                    border: "1px solid var(--clr-border)",
                    borderRadius: "var(--radius-md)",
                    background: form.porFacturar
                      ? "var(--clr-warning-bg)"
                      : "var(--clr-surface)",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.porFacturar}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, porFacturar: e.target.checked }))
                    }
                    style={{
                      width: 16,
                      height: 16,
                      accentColor: "var(--clr-warning)",
                    }}
                  />
                  <span
                    style={{
                      fontSize: "var(--fs-sm)",
                      color: form.porFacturar
                        ? "var(--clr-warning)"
                        : "var(--clr-text-secondary)",
                      fontWeight: form.porFacturar
                        ? "var(--fw-semibold)"
                        : "var(--fw-normal)",
                    }}
                  >
                    {form.porFacturar ? "Si, por facturar" : "No aplica"}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="lmd-card">
          <div className="lmd-card-header">
            <h3 className="lmd-card-title">Detalle de renglones</h3>
            <button
              type="button"
              className="lmd-btn lmd-btn-primary"
              onClick={addRow}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Agregar renglon
            </button>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                tableLayout: "fixed",
              }}
            >
              <colgroup>
                <col style={{ width: 28 }} />
                <col style={{ width: "9%" }} />
                <col />
                <col style={{ width: "6%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "6%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "6%" }} />
                <col style={{ width: "6%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "5%" }} />
              </colgroup>
              <thead>
                <tr
                  style={{
                    background: "var(--clr-bg)",
                    borderBottom: "2px solid var(--clr-border)",
                  }}
                >
                  {[
                    "",
                    "Articulo",
                    "Descripcion",
                    "UM",
                    "Clave unidad",
                    "Cod. SAT",
                    "Cant.",
                    "Precio",
                    "% Desc",
                    "% Aum",
                    "P. venta",
                    "Importe",
                    "",
                  ].map((h, i) => (
                    <th
                      key={i}
                      style={{
                        padding: "9px 10px",
                        fontSize: "var(--fs-xs)",
                        fontWeight: "var(--fw-semibold)",
                        color: "var(--clr-text-secondary)",
                        textTransform: "uppercase",
                        letterSpacing: ".4px",
                        textAlign: i >= 6 && i <= 11 ? "right" : "left",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {safeItems.map((it, idx) => {
                  const { pVenta, impDesc } = calcImportes({
                    precioBase: it.precioCompra,
                    aum: it.porcIncremento,
                    desc: it.porcDescto,
                    qty: it.cantidad,
                  });
                  return (
                    <tr
                      key={idx}
                      onDragOver={(e) => { e.preventDefault(); setDragOverIdx(idx); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragSrcIdx.current !== null) moveRow(dragSrcIdx.current, idx);
                        setDragOverIdx(null);
                        dragSrcIdx.current = null;
                      }}
                      onDragLeave={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget)) setDragOverIdx(null);
                      }}
                      style={{
                        background: dragOverIdx === idx
                          ? "#e8f0fe"
                          : idx % 2 === 0 ? "#fff" : "#fafbfc",
                        outline: dragOverIdx === idx ? "2px dashed var(--clr-primary)" : "none",
                        outlineOffset: -2,
                      }}
                    >
                      <td
                        style={{
                          ...cellStyle,
                          width: 28,
                          textAlign: "center",
                          padding: "0 4px",
                          color: "#bbb",
                          userSelect: "none",
                        }}
                      >
                        <div
                          draggable
                          onDragStart={(e) => {
                            dragSrcIdx.current = idx;
                            e.dataTransfer.effectAllowed = "move";
                            e.dataTransfer.setData("text/plain", String(idx));
                          }}
                          onDragEnd={() => {
                            setDragOverIdx(null);
                            dragSrcIdx.current = null;
                          }}
                          title="Arrastrar para reordenar"
                          style={{ cursor: "grab", fontSize: 18, lineHeight: "1", padding: "6px 2px" }}
                        >
                          ⠿
                        </div>
                      </td>
                      <td style={cellStyle}>
                        <CellInput
                          value={it.articulo}
                          placeholder="Clave"
                          onChange={(e) =>
                            onChangeItem(idx, "articulo", e.target.value)
                          }
                          onBlur={() => lookupArticulo(idx)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              lookupArticulo(idx);
                            }
                          }}
                        />
                        {rowMsgs[idx] && (
                          <p
                            style={{
                              margin: "3px 0 0",
                              fontSize: 11,
                              color: rowMsgs[idx].includes("no")
                                ? "var(--clr-danger)"
                                : "var(--clr-text-muted)",
                            }}
                          >
                            {rowMsgs[idx]}
                          </p>
                        )}
                      </td>
                      <td style={cellStyle}>
                        <CellInput
                          value={it.descripcion}
                          placeholder="Descripcion"
                          onChange={(e) =>
                            onChangeItem(idx, "descripcion", e.target.value)
                          }
                        />
                      </td>
                      <td style={cellStyle}>
                        <CellInput
                          value={it.unidadMedida}
                          placeholder="UM"
                          onChange={(e) =>
                            onChangeItem(idx, "unidadMedida", e.target.value)
                          }
                        />
                      </td>
                      <td style={cellStyle}>
                        <CellInput
                          value={it.claveUnidad}
                          placeholder="E48"
                          onChange={(e) =>
                            onChangeItem(idx, "claveUnidad", e.target.value)
                          }
                        />
                      </td>
                      <td style={cellStyle}>
                        <CellInput
                          value={it.codSat}
                          placeholder="81111508"
                          onChange={(e) =>
                            onChangeItem(idx, "codSat", e.target.value)
                          }
                        />
                      </td>
                      <td style={cellNum}>
                        <CellInput
                          value={it.cantidad}
                          type="number"
                          align="right"
                          onChange={(e) =>
                            onChangeItem(idx, "cantidad", e.target.value)
                          }
                        />
                      </td>
                      <td style={cellNum}>
                        <CellInput
                          value={it.precioCompra}
                          type="number"
                          align="right"
                          onChange={(e) =>
                            onChangeItem(idx, "precioCompra", e.target.value)
                          }
                        />
                      </td>
                      <td style={cellNum}>
                        <CellInput
                          value={it.porcDescto}
                          type="number"
                          align="right"
                          onChange={(e) =>
                            onChangeItem(idx, "porcDescto", e.target.value)
                          }
                        />
                      </td>
                      <td style={cellNum}>
                        <CellInput
                          value={it.porcIncremento}
                          type="number"
                          align="right"
                          onChange={(e) =>
                            onChangeItem(idx, "porcIncremento", e.target.value)
                          }
                        />
                      </td>
                      <td
                        style={{
                          ...cellNum,
                          fontFamily: "var(--font-mono)",
                          fontSize: "var(--fs-xs)",
                          color: "var(--clr-text-secondary)",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fmtMX(pVenta)}
                      </td>
                      <td
                        style={{
                          ...cellNum,
                          fontFamily: "var(--font-mono)",
                          fontWeight: "var(--fw-semibold)",
                          color: "var(--clr-text-primary)",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fmtMX(impDesc)}
                      </td>
                      <td style={{ ...cellStyle, textAlign: "center" }}>
                        <button
                          type="button"
                          onClick={() => openDeleteModal(idx)}
                          style={{
                            border: "none",
                            background: "none",
                            cursor: "pointer",
                            color: "var(--clr-danger)",
                            padding: 4,
                            borderRadius: "var(--radius-sm)",
                            fontSize: 18,
                            lineHeight: 1,
                          }}
                          title="Quitar"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="lmd-card">
          <div className="lmd-card-body">
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <div style={{ width: 280, display: "grid", gap: "var(--sp-2)" }}>
                {[
                  { label: "Subtotal", value: subtotal },
                  { label: "IVA 16%", value: iva },
                ].map(({ label, value }) => (
                  <div
                    key={label}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "6px 0",
                      borderBottom: "1px solid var(--clr-border-subtle)",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "var(--fs-sm)",
                        color: "var(--clr-text-secondary)",
                      }}
                    >
                      {label}
                    </span>
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontVariantNumeric: "tabular-nums",
                        fontSize: "var(--fs-sm)",
                      }}
                    >
                      ${fmtMX(value)}
                    </span>
                  </div>
                ))}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 0 4px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "var(--fw-bold)",
                      fontSize: "var(--fs-md)",
                    }}
                  >
                    Total
                  </span>
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontWeight: "var(--fw-extrabold)",
                      fontSize: "var(--fs-xl)",
                      color: "var(--clr-primary)",
                    }}
                  >
                    ${fmtMX(total)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "var(--sp-3)",
            flexWrap: "wrap",
            paddingBottom: "var(--sp-8)",
          }}
        >
          <button
            type="submit"
            className="lmd-btn lmd-btn-primary"
            disabled={saving}
          >
            {saving ? (
              <>
                <div
                  className="lmd-spinner"
                  style={{ width: 14, height: 14 }}
                />{" "}
                Guardando...
              </>
            ) : (
              <>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                Guardar cambios
              </>
            )}
          </button>
          <button
            type="button"
            className="lmd-btn lmd-btn-secondary"
            onClick={() => navigate("/cotizaciones")}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="lmd-btn"
            style={{
              background: "var(--clr-info-bg)",
              color: "var(--clr-info)",
              border: "1px solid #bae6fd",
            }}
            onClick={() => setShowProfitModal(true)}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
            Ver Utilidad
          </button>
          <button
            type="button"
            className="lmd-btn lmd-btn-secondary"
            onClick={handlePrint}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            Imprimir / PDF
          </button>
          <button
            type="button"
            className="lmd-btn"
            style={{ background: "#eff6ff", color: "#2563eb", border: "1px solid #bae6fd" }}
            onClick={handleAbrirEmail}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
            Enviar por correo
          </button>
          <button
            type="button"
            className="lmd-btn"
            style={{
              background: "#f0fdf4",
              color: "#16a34a",
              border: "1px solid #bbf7d0",
            }}
            onClick={handleSincronizarCatalogo}
            disabled={syncing}
          >
            {syncing ? (
              <>
                <div className="lmd-spinner" style={{ width: 14, height: 14 }} />{" "}
                Sincronizando...
              </>
            ) : (
              <>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                Exportar al catálogo
              </>
            )}
          </button>
          <button
            type="button"
            className="lmd-btn"
            style={{
              background: "var(--clr-warning-bg)",
              color: "var(--clr-warning)",
              border: "1px solid #fde68a",
            }}
            onClick={handleDuplicar}
            disabled={duplicating}
          >
            {duplicating ? (
              <>
                <div className="lmd-spinner" style={{ width: 14, height: 14 }} />{" "}
                Duplicando...
              </>
            ) : (
              <>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                Duplicar
              </>
            )}
          </button>
        </div>
      </form>

      {/* Modal Enviar Correo */}
      {showEmailModal && (
        <div className="lmd-modal-overlay" onClick={() => !sendingEmail && setShowEmailModal(false)}>
          <div className="lmd-modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <div className="lmd-modal-header">
              <h3 className="lmd-modal-title">Enviar cotización por correo</h3>
              <button className="lmd-modal-close" onClick={() => setShowEmailModal(false)} disabled={sendingEmail}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="lmd-modal-body" style={{ display: "grid", gap: "var(--sp-3)" }}>
              <div className="lmd-field">
                <label className="lmd-label">Para (correo del cliente) *</label>
                <input
                  className="lmd-input"
                  type="text"
                  placeholder="correo1@empresa.com;correo2@empresa.com"
                  value={emailForm.destinatario}
                  onChange={(e) => setEmailForm((f) => ({ ...f, destinatario: e.target.value }))}
                  disabled={sendingEmail}
                />
              </div>
              <div className="lmd-field">
                <label className="lmd-label">Asunto</label>
                <input
                  className="lmd-input"
                  value={emailForm.asunto}
                  onChange={(e) => setEmailForm((f) => ({ ...f, asunto: e.target.value }))}
                  disabled={sendingEmail}
                />
              </div>
              <div className="lmd-field">
                <label className="lmd-label">Mensaje</label>
                <textarea
                  className="lmd-input"
                  rows={8}
                  style={{ resize: "vertical", fontFamily: "var(--font-sans)", fontSize: "var(--fs-sm)", lineHeight: 1.6 }}
                  value={emailForm.cuerpo}
                  onChange={(e) => setEmailForm((f) => ({ ...f, cuerpo: e.target.value }))}
                  disabled={sendingEmail}
                />
              </div>
              <p style={{ margin: 0, fontSize: "var(--fs-xs)", color: "var(--clr-text-muted)" }}>
                Se enviará desde <strong>ventas@lmdint.mx</strong>
              </p>
            </div>
            <div className="lmd-modal-footer">
              <button className="lmd-btn lmd-btn-secondary" onClick={() => setShowEmailModal(false)} disabled={sendingEmail}>Cancelar</button>
              <button
                className="lmd-btn"
                style={{ background: "#eff6ff", color: "#2563eb", border: "1px solid #bae6fd" }}
                onClick={handleEnviarEmail}
                disabled={sendingEmail}
              >
                {sendingEmail ? (
                  <><div className="lmd-spinner" style={{ width: 13, height: 13 }} /> Enviando...</>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                    </svg>
                    Enviar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmar Sincronizar */}
      {showSyncModal && (
        <div className="lmd-modal-overlay" onClick={() => setShowSyncModal(false)}>
          <div className="lmd-modal lmd-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="lmd-modal-header">
              <h3 className="lmd-modal-title">Exportar al catálogo</h3>
              <button className="lmd-modal-close" onClick={() => setShowSyncModal(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="lmd-modal-body">
              <p style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--clr-text-secondary)" }}>
                ¿Sincronizar <strong>{safeItems.filter((it) => it.articulo?.trim()).length} artículo(s)</strong> al catálogo?
                <br /><br />
                Los que ya existen se actualizarán (descripción, precio, unidad, cod. SAT).
                Los que no existen se darán de alta.
              </p>
            </div>
            <div className="lmd-modal-footer">
              <button className="lmd-btn lmd-btn-secondary" onClick={() => setShowSyncModal(false)}>Cancelar</button>
              <button className="lmd-btn lmd-btn-primary" onClick={confirmarSincronizar}>Sincronizar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmar Duplicar */}
      {showDuplicarModal && (
        <div className="lmd-modal-overlay" onClick={() => setShowDuplicarModal(false)}>
          <div className="lmd-modal lmd-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="lmd-modal-header">
              <h3 className="lmd-modal-title">Duplicar cotización</h3>
              <button className="lmd-modal-close" onClick={() => setShowDuplicarModal(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="lmd-modal-body">
              <p style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--clr-text-secondary)" }}>
                ¿Duplicar la cotización <strong>#{cotNum ?? id}</strong>? Se creará una nueva con el siguiente consecutivo.
              </p>
            </div>
            <div className="lmd-modal-footer">
              <button className="lmd-btn lmd-btn-secondary" onClick={() => setShowDuplicarModal(false)}>Cancelar</button>
              <button
                className="lmd-btn"
                style={{ background: "var(--clr-warning-bg)", color: "var(--clr-warning)", border: "1px solid #fde68a" }}
                onClick={confirmarDuplicar}
              >
                Duplicar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Utilidad */}
      {showProfitModal && (
        <div
          className="lmd-modal-overlay"
          onClick={() => setShowProfitModal(false)}
        >
          <div
            className="lmd-modal lmd-modal-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lmd-modal-header">
              <h3 className="lmd-modal-title">
                Utilidad — Cotizacion #{cotNum ?? id}
              </h3>
              <button
                className="lmd-modal-close"
                onClick={() => setShowProfitModal(false)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div
              className="lmd-modal-body"
              style={{ display: "grid", gap: "var(--sp-3)" }}
            >
              {[
                {
                  label: "Importe Total de Compra",
                  value: totalProfit.totalImporteCompra,
                  color: "var(--clr-danger)",
                },
                {
                  label: "Importe Total de Venta (Neto)",
                  value: totalProfit.totalImporteVenta,
                  color: "var(--clr-success)",
                },
              ].map(({ label, value, color }) => (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "10px 0",
                    borderBottom: "1px solid var(--clr-border-subtle)",
                  }}
                >
                  <span
                    style={{
                      fontSize: "var(--fs-sm)",
                      color: "var(--clr-text-secondary)",
                    }}
                  >
                    {label}
                  </span>
                  <strong style={{ color, fontFamily: "var(--font-mono)" }}>
                    ${fmtMX(value)}
                  </strong>
                </div>
              ))}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "12px 0 4px",
                }}
              >
                <span style={{ fontWeight: "var(--fw-bold)" }}>
                  Utilidad Total
                </span>
                <strong
                  style={{
                    fontSize: "var(--fs-xl)",
                    color:
                      totalProfit.utilidad < 0
                        ? "var(--clr-danger)"
                        : "var(--clr-primary)",
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  ${fmtMX(totalProfit.utilidad)}
                </strong>
              </div>
            </div>
            <div className="lmd-modal-footer">
              <button
                className="lmd-btn lmd-btn-primary"
                onClick={() => setShowProfitModal(false)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Eliminar */}
      {showDeleteModal && rowToDelete !== null && (
        <div className="lmd-modal-overlay" onClick={closeDeleteModal}>
          <div
            className="lmd-modal lmd-modal-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lmd-modal-header">
              <h3 className="lmd-modal-title">Confirmar eliminacion</h3>
              <button className="lmd-modal-close" onClick={closeDeleteModal}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="lmd-modal-body">
              <p
                style={{
                  margin: 0,
                  fontSize: "var(--fs-sm)",
                  color: "var(--clr-text-secondary)",
                }}
              >
                Deseas quitar el renglon con articulo{" "}
                <strong style={{ color: "var(--clr-text-primary)" }}>
                  {safeItems[rowToDelete]?.articulo || "sin clave"}
                </strong>
                ? Esta accion no se puede deshacer.
              </p>
            </div>
            <div className="lmd-modal-footer">
              <button
                className="lmd-btn lmd-btn-secondary"
                onClick={closeDeleteModal}
              >
                Cancelar
              </button>
              <button className="lmd-btn lmd-btn-danger" onClick={confirmRmRow}>
                Quitar renglon
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
